import { useMemo } from 'react';
import { PrefillType } from '@weave/schema-gen-ts/dist/schemas/payments-platform/care-credit-integration/care_credit_integration.pb';
import { Person } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons.pb';
import dayjs from 'dayjs';
import { CareCreditIntegrationCustom } from '@frontend/api-care-credit-integration';
import { InvoiceModel } from '@frontend/api-invoices';
import { CareCreditPatientData } from '../types/care-credit.types';
import { getQuickOfferDecisionStatus } from '../widgets/care-credit-enrollment-multi-step/utils/quick-offer';
import { useCareCreditPurlQuery } from './use-care-credit-purl-query';

export type UseCareCreditPatientDataProps = {
  person: Person | undefined;
  locationId: string | undefined;
  merchantNumber: string | undefined;
  invoice?: InvoiceModel;
};
export type UseCareCreditPatientDataOptions = {
  enabled?: boolean;
  usePurlFallback?: boolean;
  prefillType?: PrefillType;
};

export const useCareCreditPatientData = (
  { person, locationId, merchantNumber, invoice }: UseCareCreditPatientDataProps,
  { enabled, usePurlFallback = true, prefillType }: UseCareCreditPatientDataOptions = {}
) => {
  const {
    query: { isLoading, isRefetching, isFetching, isSuccess, error, data },
    isPersonValid,
  } = CareCreditIntegrationCustom.useCareCreditPatientDataQuery(
    {
      person,
      merchantNumber,
    },
    {
      enabled,
      refetchOnMount: 'always',
    }
  );

  const { purl, isPurlLoading } = useCareCreditPurlQuery(
    {
      locationId,
      invoice,
      prefillType,
      person,
    },
    {
      enabled:
        !!enabled &&
        usePurlFallback &&
        (prefillType !== PrefillType.PREFILL_TYPE_UNSPECIFIED ||
          (!isLoading && !isRefetching && !isFetching && !data?.locationUrl)),
    }
  );

  const careCreditData = useMemo<CareCreditPatientData | undefined>(() => {
    if (!data) {
      return undefined;
    }

    const status = getQuickOfferDecisionStatus(data);

    if (status === 'approved') {
      return {
        personId: person?.personId || '',
        status,
        amount: Number(data.errorResponse?.accountInfo?.creditLine) || 0,
        careCreditPersonId: data.errorResponse?.accountInfo?.accountNumber || '',
        asOfDate: dayjs().format('YYYY-MM-DD HH:mm:ss'),
        patientLink: purl?.purl || data.locationUrl || '',
      };
    }
    return {
      personId: person?.personId || '',
      status: getQuickOfferDecisionStatus(data),
      amount: data.plccOfferInfo?.creditLine ? Number(data.plccOfferInfo.creditLine) : 0,
      careCreditPersonId: '',
      asOfDate: dayjs().format('YYYY-MM-DD HH:mm:ss'),
      patientLink: purl?.purl || data.locationUrl || '',
    };
  }, [person?.personId, purl?.purl, data]);

  return {
    isBusy: isLoading || isRefetching || isFetching || isPurlLoading,
    isLoading: isLoading || isPurlLoading,
    isSuccess,
    error,
    isPersonValid,
    errorResponse: data?.errorResponse,
    careCreditData,
  };
};

export type UseCareCreditPatientData = ReturnType<typeof useCareCreditPatientData>;
